import { Workflow, MousePointerClick, Users } from "lucide-react";
import { SectionHead } from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";

const SERVICES = [
    {
        num: "01",
        icon: Workflow,
        title: "AI Exception Architecture",
        focus: "Lean Six Sigma & Business Analysis",
        approach:
            "We map your new AI-driven processes to identify statistical points of failure. Instead of waiting for the system to break, we proactively design the exact escalation points and Standard Operating Procedures (SOPs) required for human intervention.",
        testid: "service-card-ai-exception-architecture",
    },
    {
        num: "02",
        icon: MousePointerClick,
        title: "The Seamless Digital Handoff",
        focus: "WalkMe Builder & Instructional Design",
        approach:
            "When an AI flags an exception, your employees need to know exactly how to resolve it instantly. We build targeted in-app guidance directly into the flow of work. Your team is empowered to resolve the exception without leaving their screen, ensuring zero user confusion and seamless software adoption.",
        testid: "service-card-seamless-digital-handoff",
    },
    {
        num: "03",
        icon: Users,
        title: "Human Alignment & Behavioral Integration",
        focus: "MBTI, FIRO & Human Performance Improvement (HPI)",
        approach:
            "Transitioning a team from “doing the work” to “managing the AI's exceptions” changes the fundamental nature of their jobs. We use objective behavioral diagnostics to help leaders realign their teams, matching cognitive styles to the fast-paced demands of exception resolution and eliminating interpersonal friction.",
        testid: "service-card-human-alignment",
    },
];

export default function Services() {
    return (
        <section id="services" className="relative py-24 sm:py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHead
                    index="03"
                    label="Our Services"
                    title={
                        <>
                            How We Engineer <span className="text-blaze">the Handoff</span>
                        </>
                    }
                />

                <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-3">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.num} delay={i * 0.12} className="h-full">
                            <article
                                data-testid={s.testid}
                                className="group relative flex h-full flex-col border border-line bg-panel/60 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-blaze/60 hover:bg-panelhi hover:shadow-[0_20px_50px_-20px_rgba(255,94,26,0.25)]"
                            >
                                <span className="pointer-events-none absolute right-6 top-5 font-display text-6xl font-extrabold leading-none text-white/[0.06] transition-colors duration-300 group-hover:text-blaze/20">
                                    {s.num}
                                </span>

                                <div className="flex h-12 w-12 items-center justify-center border border-line bg-ink text-signal transition-colors duration-300 group-hover:border-blaze/50 group-hover:text-blaze">
                                    <s.icon className="h-6 w-6" strokeWidth={1.75} />
                                </div>

                                <h3 className="mt-8 min-h-[4.6rem] font-display text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-[1.7rem]">
                                    {s.title}
                                </h3>

                                <div className="mt-6">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">
                                        The Focus
                                    </div>
                                    <div className="mt-2 text-sm font-semibold text-slate-200">
                                        {s.focus}
                                    </div>
                                </div>

                                <div className="mt-6 flex-1 border-t border-line pt-6">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                        The Approach
                                    </div>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                        {s.approach}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
